/**
 * WebSocket client with exponential backoff reconnect and ping/pong heartbeat.
 */

export type WsRole = "host" | "remote";

export type WsMessage = {
  type: string;
  data?: unknown;
  playing?: boolean;
  isPaid?: boolean;
};

export type WsConnectionStatus =
  | "idle"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "closed";

type Handler = (msg: WsMessage) => void;

export function getWsUrl(): string {
  const wsProtocol = location.protocol === "https:" ? "wss:" : "ws:";
  return `${wsProtocol}//${location.host}/ws`;
}

export class WsClient {
  private ws: WebSocket | null = null;
  private handlers = new Map<string, Set<Handler>>();
  private statusHandlers = new Set<(status: WsConnectionStatus, attempt: number) => void>();
  private url = "";
  private code = "";
  private role: WsRole = "remote";
  private intentionalClose = false;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private pongTimeout: ReturnType<typeof setTimeout> | null = null;
  private status: WsConnectionStatus = "idle";

  connect(code: string, role: WsRole, baseUrl?: string): void {
    this.code = code.toUpperCase();
    this.role = role;
    this.url = baseUrl || getWsUrl();
    this.intentionalClose = false;
    this.reconnectAttempt = 0;
    this.openSocket();
  }

  disconnect(): void {
    this.intentionalClose = true;
    this.clearTimers();
    if (this.ws) {
      this.ws.close(1000, "client disconnect");
      this.ws = null;
    }
    this.setStatus("closed");
  }

  send(msg: WsMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  on(type: string, handler: Handler): () => void {
    if (!this.handlers.has(type)) this.handlers.set(type, new Set());
    this.handlers.get(type)!.add(handler);
    return () => this.handlers.get(type)?.delete(handler);
  }

  onStatus(handler: (status: WsConnectionStatus, attempt: number) => void): () => void {
    this.statusHandlers.add(handler);
    handler(this.status, this.reconnectAttempt);
    return () => this.statusHandlers.delete(handler);
  }

  getStatus(): WsConnectionStatus {
    return this.status;
  }

  private setStatus(status: WsConnectionStatus): void {
    this.status = status;
    for (const h of this.statusHandlers) h(status, this.reconnectAttempt);
  }

  private openSocket(): void {
    this.clearReconnectTimer();
    const isReconnect = this.reconnectAttempt > 0;
    this.setStatus(isReconnect ? "reconnecting" : "connecting");

    const wsUrl = `${this.url}?code=${encodeURIComponent(this.code)}&role=${this.role}`;
    const ws = new WebSocket(wsUrl);
    this.ws = ws;

    ws.addEventListener("open", () => {
      this.reconnectAttempt = 0;
      this.setStatus("connected");
      this.startPing();
    });

    ws.addEventListener("message", (ev) => {
      let msg: WsMessage;
      try {
        msg = JSON.parse(String(ev.data));
      } catch {
        return;
      }
      if (msg.type === "ping") {
        this.send({ type: "pong" });
        return;
      }
      if (msg.type === "pong") {
        this.clearPongTimeout();
        return;
      }
      const set = this.handlers.get(msg.type);
      if (set) for (const h of set) h(msg);
      const all = this.handlers.get("*");
      if (all) for (const h of all) h(msg);
    });

    ws.addEventListener("close", () => {
      this.stopPing();
      this.ws = null;
      if (this.intentionalClose) {
        this.setStatus("closed");
        return;
      }
      this.scheduleReconnect();
    });

    ws.addEventListener("error", () => {
      // close handler will schedule reconnect
    });
  }

  private scheduleReconnect(): void {
    if (this.intentionalClose) return;
    this.reconnectAttempt += 1;
    this.setStatus("reconnecting");
    const delay = Math.min(1000 * 2 ** (this.reconnectAttempt - 1), 30_000);
    this.reconnectTimer = setTimeout(() => this.openSocket(), delay);
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      if (this.ws?.readyState !== WebSocket.OPEN) return;
      this.send({ type: "ping" });
      this.clearPongTimeout();
      this.pongTimeout = setTimeout(() => {
        this.ws?.close(4003, "pong timeout");
      }, 10_000);
    }, 25_000);
  }

  private stopPing(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
    this.clearPongTimeout();
  }

  private clearPongTimeout(): void {
    if (this.pongTimeout) clearTimeout(this.pongTimeout);
    this.pongTimeout = null;
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private clearTimers(): void {
    this.stopPing();
    this.clearReconnectTimer();
  }
}
